import { type AxiosResponse } from 'axios';
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';

import { APIError, ResourceWithId } from '@api/types';
import { post, get, put, patch } from '@api/httpClient';
import queryClient from '@api/queryClient';
import { type SampleResultSchema, type SampleSchema } from '@/zod_schemas';
import {
  type SampleSearchResponse,
  type SampleSearchResult,
  type SearchFieldsType,
} from './types';

export const SAMPLE_PICKER_RESULT_LIMIT = 25;
const RECENT_SAMPLE_WINDOW_DAYS = 90;

const QUERY_KEYS = {
  SAMPLES: ['samples'],
  SAMPLE: (sampleId: string) => ['samples', { id: sampleId }],
  SAMPLE_SEARCH: (search: string) =>
    ['samples', 'picker', { search: search || 'recent' }] as const,
  PATIENT_SAMPLES: (patientId: string) =>
    ['patient_samples', { patientId }] as const,
};

// export const useCreateSample = () =>
//   useMutation<AxiosResponse<SampleSchema[]>, APIError, SampleSchema[]>({
//     mutationFn: (samples) => post<SampleSchema[]>('/samples/', samples),
//   });

export const useCreateSample = (patientId?: string) =>
  useMutation<AxiosResponse<SampleSchema[]>, APIError, SampleSchema[]>({
    mutationFn: async (samples) => post<SampleSchema[]>('/samples/', samples),
    onSuccess: async () => {
      if (!patientId) return;

      //  re-fetch patient HPO list from server
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PATIENT_SAMPLES(patientId),
      });
    },
  });

interface RemoveSamplesPayload {
  id: string; // Patient ID
  sample_ids?: string[]; // List of sample IDs
}

export const usePatchPatientSamples = (patientId?: string) =>
  useMutation<AxiosResponse<string[]>, APIError, RemoveSamplesPayload>({
    mutationFn: async ({ id, sample_ids }) =>
      post<string[]>(`/patient/${id}/remove_samples/`, {
        sample_ids,
      }),
    onSuccess: async () => {
      if (!patientId) return;

      //  re-fetch patient HPO list from server
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PATIENT_SAMPLES(patientId),
      });
    },
  });

export const useUpdateSample = () =>
  useMutation<
    AxiosResponse<SampleSchema>,
    APIError,
    ResourceWithId<SampleSchema>
  >({
    mutationFn: ({ id, payload }) =>
      put<SampleSchema>(`/samples/${id}/`, { ...payload }),
    onSuccess: ({ data: { id } }) => {
      if (!id) return;

      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.SAMPLES,
      });
    },
  });

export const useListSamples = () =>
  useSuspenseQuery<SampleSchema[]>({
    queryKey: QUERY_KEYS.SAMPLES,
    queryFn: async () => {
      const resp = await get<SampleSchema[]>('/samples/');
      return resp.data;
    },
  });

export const useGetSample = (sampleId?: string) =>
  useSuspenseQuery<SampleSchema>({
    queryKey: QUERY_KEYS.SAMPLE(sampleId || ''),
    queryFn: async () => {
      if (!sampleId) return {};

      const resp = await get(`/samples/${sampleId}/`);
      return resp.data;
    },
  });

export const useSearchSampleID = (sampleId = '', enabled = true) => {
  const normalizedSearch = sampleId.trim();

  return useQuery<SampleSearchResult[]>({
    queryKey: QUERY_KEYS.SAMPLE_SEARCH(normalizedSearch),
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams({
        active: 'true',
        ordering: '-updated_at',
      });

      if (normalizedSearch) {
        params.set('search', normalizedSearch);
      } else {
        const recentSampleStart = new Date();
        recentSampleStart.setDate(
          recentSampleStart.getDate() - RECENT_SAMPLE_WINDOW_DAYS,
        );
        params.set('updated_at_after', recentSampleStart.toISOString());
      }

      const resp = await get<SampleSearchResponse>('/samples/', {
        params,
        signal,
      });
      const samples = Array.isArray(resp.data) ? resp.data : resp.data.results;
      const sampleIdSearch = normalizedSearch.toLocaleLowerCase();
      const matchingSamples = normalizedSearch
        ? samples.filter((sample) =>
            sample.sample_id.toLocaleLowerCase().includes(sampleIdSearch),
          )
        : samples;
      const exactMatch = matchingSamples.find(
        (sample) => sample.sample_id.toLocaleLowerCase() === sampleIdSearch,
      );
      const prioritizedSamples = exactMatch
        ? [
            exactMatch,
            ...matchingSamples.filter((sample) => sample.id !== exactMatch.id),
          ]
        : matchingSamples;

      return prioritizedSamples.slice(0, SAMPLE_PICKER_RESULT_LIMIT);
    },
    enabled,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });
};

export const useGetPatientSample = (sampleId?: string) =>
  useSuspenseQuery<SampleSchema[]>({
    queryKey: QUERY_KEYS.PATIENT_SAMPLES(sampleId || ''),
    queryFn: async () => {
      if (!sampleId) return {};

      const resp = await get(`/samples/${sampleId}/by_patient/`);
      return resp.data;
    },
  });

export const usePatchSample = () =>
  useMutation<
    AxiosResponse<SampleSchema>,
    APIError,
    ResourceWithId<SampleSchema>
  >({
    mutationFn: ({ id, payload }) =>
      patch<SampleSchema>(`/samples/${id}/`, { ...payload }),
    onSuccess: ({ data: { id } }) => {
      if (!id) return;

      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.SAMPLES,
      });
    },
  });
export const usePutSample = () =>
  useMutation<
    AxiosResponse<SampleSchema>,
    APIError,
    ResourceWithId<SampleSchema>
  >({
    mutationFn: ({ id, payload }) =>
      put<SampleSchema>(`/samples/${id}/`, { ...payload }),
    onSuccess: ({ data: { id } }) => {
      if (!id) return;

      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.SAMPLES,
      });
    },
  });

const QUERY_KEYS_EXPERIMENT_SAMPLE = {
  experiment_sample_results: ['experiment_sample_results'],
  SAMPLEID: (sampleId: string, sampleDatabaseId?: string) => [
    'experiment_sample_results',
    { id: sampleId, sampleDatabaseId },
  ],
};

// export const useGetExperimentsBySample = (searchFields: SearchFieldsType) =>
//   useSuspenseQuery<SampleResultSchema>({
//     queryKey: QUERY_KEYS_EXPERIMENT_SAMPLE.SAMPLEID(searchFields.id || ''),
//     queryFn: async () => {
//       if (!searchFields.id) return {};

//       const resp = await get(`/experiments/sample/?search=${searchFields.id}`);
//       return resp.data;
//     },
//   });

export const useGetExperimentsBySample = (
  searchFields: SearchFieldsType,
  enabled: boolean,
  sampleDatabaseId?: string,
) =>
  useQuery<SampleResultSchema[]>({
    queryKey: QUERY_KEYS_EXPERIMENT_SAMPLE.SAMPLEID(
      searchFields.id || '',
      sampleDatabaseId,
    ),
    queryFn: async ({ signal }) => {
      if (!searchFields.id || !sampleDatabaseId) return [];
      const resp = await get<SampleResultSchema[]>('/experiments/sample/', {
        params: { search: searchFields.id },
        signal,
      });

      return resp.data.filter((experiment) =>
        experiment.samples.some((sample) => sample.id === sampleDatabaseId),
      );
    },
    retry: 1,
    enabled: enabled && Boolean(sampleDatabaseId),
    staleTime: 60 * 1000,
  });

interface RemoveSamplesPayload {
  id: string; // Patient ID
  sample_ids?: string[]; // List of sample IDs
}

export const useAssignPatientSamples = (patientId?: string) =>
  useMutation<AxiosResponse<string[]>, APIError, RemoveSamplesPayload>({
    mutationFn: async ({ id, sample_ids }) =>
      post<string[]>(`/patient/${id}/assign_samples/`, {
        sample_ids,
      }),
    onSuccess: async () => {
      if (!patientId) return;

      //  re-fetch patient HPO list from server
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PATIENT_SAMPLES(patientId),
      });
    },
  });
